import { PublicAppSubStoreArgs, PublicAppSubStore } from "@/store/types";
import { action, computed, makeObservable, observable, runInAction } from "mobx";
import {
  RecallPermissionKind,
  RecallPermissionMap,
  RecallPermissionStatus,
} from "@/modules/electron/types";
import { DEFAULT_SYSTEM_PERMISSIONS_MAP } from "@/store/system-permissions/system-permissions-store.constants";
import { AppStoreSystemPermissionsUiStore } from "@/store/system-permissions/ui/system-permissions-ui.store";
import { electronModule } from "@/modules/electron";

export class AppStoreSystemPermissionsStore extends PublicAppSubStore {
  permissionMap: RecallPermissionMap = DEFAULT_SYSTEM_PERMISSIONS_MAP;
  calendarNotificationEnabled = false;
  meetingNotificationEnabled = false;
  teamsMeetingDetectionEnabled = false;
  ui: AppStoreSystemPermissionsUiStore;

  constructor(injectedDeps: PublicAppSubStoreArgs) {
    super(injectedDeps);

    this.ui = new AppStoreSystemPermissionsUiStore(injectedDeps);

    makeObservable<AppStoreSystemPermissionsStore>(this, {
      ui: observable,
      permissionMap: observable,
      calendarNotificationEnabled: observable,
      meetingNotificationEnabled: observable,
      teamsMeetingDetectionEnabled: observable,
      hasSystemAudioRecordingPermission: computed,
      hasMicrophoneRecordingPermission: computed,
      hasAccessibilityPermission: computed,
      hasRequiredVoicePermissions: computed,
      hasRequiredMeetingPermissions: computed,
      hasRequiredAdvancedMeetingPermissions: computed,
      permissionsAreApplicableForDevice: computed,
      setPermissionMap: action,
      hasScreenCapturePermission: computed,
      hasScreenCaptureOrSystemAudioPermission: computed,
      hasFullDiskAccessPermission: computed,
      setCalendarNotificationEnabled: action,
      setMeetingNotificationEnabled: action,
      setTeamsMeetingDetectionEnabled: action,
      initialize: action,
      resetState: action,
      syncPermissions: action,
    });
  }

  setPermissionMap({ permissionMap }: { permissionMap: RecallPermissionMap }) {
    const screenCapturePermission =
      permissionMap?.[RecallPermissionKind.ScreenCapture] ?? RecallPermissionStatus.NotRequested;

    const microphonePermission =
      permissionMap?.[RecallPermissionKind.Microphone] ?? RecallPermissionStatus.NotRequested;

    const accessibilityPermission =
      permissionMap?.[RecallPermissionKind.Accessibility] ?? RecallPermissionStatus.NotRequested;

    const systemAudioPermission =
      permissionMap?.[RecallPermissionKind.SystemAudio] ?? RecallPermissionStatus.NotRequested;

    const fullDiskAccessPermission =
      permissionMap?.[RecallPermissionKind.FullDiskAccess] ?? RecallPermissionStatus.NotRequested;

    this.permissionMap = {
      [RecallPermissionKind.ScreenCapture]: screenCapturePermission,
      [RecallPermissionKind.Microphone]: microphonePermission,
      [RecallPermissionKind.Accessibility]: accessibilityPermission,
      [RecallPermissionKind.SystemAudio]: systemAudioPermission,
      [RecallPermissionKind.FullDiskAccess]: fullDiskAccessPermission,
    };
  }

  setCalendarNotificationEnabled({ enabled }: { enabled: boolean }) {
    this.calendarNotificationEnabled = enabled;
  }

  setMeetingNotificationEnabled({ enabled }: { enabled: boolean }) {
    this.meetingNotificationEnabled = enabled;
  }

  setTeamsMeetingDetectionEnabled({ enabled }: { enabled: boolean }) {
    this.teamsMeetingDetectionEnabled = enabled;
  }

  get hasScreenCapturePermission(): boolean {
    return (
      this.permissionMap?.[RecallPermissionKind.ScreenCapture] === RecallPermissionStatus.Granted
    );
  }

  get hasSystemAudioRecordingPermission(): boolean {
    return (
      this.permissionMap?.[RecallPermissionKind.SystemAudio] === RecallPermissionStatus.Granted
    );
  }

  get hasScreenCaptureOrSystemAudioPermission(): boolean {
    return this.hasScreenCapturePermission || this.hasSystemAudioRecordingPermission;
  }

  get hasMicrophoneRecordingPermission(): boolean {
    return this.permissionMap?.[RecallPermissionKind.Microphone] === RecallPermissionStatus.Granted;
  }

  get hasAccessibilityPermission(): boolean {
    return (
      this.permissionMap?.[RecallPermissionKind.Accessibility] === RecallPermissionStatus.Granted
    );
  }

  get hasFullDiskAccessPermission(): boolean {
    return (
      this.permissionMap?.[RecallPermissionKind.FullDiskAccess] === RecallPermissionStatus.Granted
    );
  }

  get permissionsAreApplicableForDevice(): boolean {
    /**
     * The permission checks are a only relevant to MacOS (on Desktop).
     *
     * We also don't support permission-checking on Intel-based MacOS devices, because
     * we use the Recall AI Desktop SDK to check permissions (and they don't support it).
     */

    const isDesktop = this.store.interface.isDesktop;
    const isMacOs = this.store.interface.isMacOs;
    const isIntelMacDesktop = this.store.electron.isIntelMacDesktop;

    if (!isDesktop) {
      return false;
    }

    if (!isMacOs) {
      return false;
    }

    if (isIntelMacDesktop) {
      return false;
    }

    return true;
  }

  get hasRequiredVoicePermissions(): boolean {
    if (!this.permissionsAreApplicableForDevice) {
      return true;
    }

    if (!this.hasMicrophoneRecordingPermission) {
      return false;
    }

    if (!this.hasAccessibilityPermission) {
      return false;
    }

    /**
     * As long as the user has either system audio recording or screen capture permission,
     * we'll be able to record audio.
     */
    if (!this.hasScreenCaptureOrSystemAudioPermission) {
      return false;
    }

    return true;
  }

  get hasRequiredMeetingPermissions(): boolean {
    if (!this.permissionsAreApplicableForDevice) {
      return true;
    }

    if (!this.hasAccessibilityPermission) {
      return false;
    }

    return true;
  }

  get hasRequiredAdvancedMeetingPermissions(): boolean {
    if (!this.permissionsAreApplicableForDevice) {
      return true;
    }

    /**
     * For `teamsMeetingDetectionEnabled`, we need an extra `FullDiskAccess` permission.
     */
    if (!this.hasFullDiskAccessPermission) {
      return false;
    }

    return this.hasRequiredMeetingPermissions;
  }

  async syncPermissions() {
    const permissionMap = await electronModule.recall.getPermissionStatus();

    runInAction(() => {
      this.setPermissionMap({ permissionMap });
    });
  }

  initialize = async () => {};

  resetState = async () => {
    runInAction(() => {
      this.permissionMap = DEFAULT_SYSTEM_PERMISSIONS_MAP;
      this.ui.resetState();
    });
  };
}
